import { useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useRoomStore, useGuestStore, useAuthStore } from '@/stores'
import { MessageSquare, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Room } from '@core/models'

interface RoomSidebarProps {
  selectedRoomId: string
  onSelectRoom: (roomId: string) => void
}

const formatTime = (dateString?: string) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  if (date.toDateString() === now.toDateString()) {
    return new Intl.DateTimeFormat('ja-JP', { hour: '2-digit', minute: '2-digit' }).format(date)
  }
  return new Intl.DateTimeFormat('ja-JP', { month: '2-digit', day: '2-digit' }).format(date)
}

const RoomItem = observer(({ room, selected, onSelect }: { room: Room; selected: boolean; onSelect: () => void }) => {
  const guestStore = useGuestStore()

  useEffect(() => {
    if (room.guest && !guestStore.guests.has(room.guest)) {
      void guestStore.getGuest(room.tenant, room.guest)
    }
  }, [room.guest, room.tenant, guestStore])

  const guest = room.guest ? guestStore.guests.get(room.guest) : undefined
  const name = guest?.name || '(読み込み中...)'

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full text-left px-3 py-2 rounded-md transition-colors',
        selected ? 'bg-primary/15' : 'hover:bg-accent'
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm truncate">{name}</span>
        <span className="text-[11px] text-muted-foreground whitespace-nowrap">
          {formatTime(room.last_message_at || room.created)}
        </span>
      </div>
      <div className="text-xs text-muted-foreground truncate mt-0.5">
        {room.last_message_preview || '(メッセージなし)'}
      </div>
    </button>
  )
})

const RoomSidebar = observer(({ selectedRoomId, onSelectRoom }: RoomSidebarProps) => {
  const roomStore = useRoomStore()
  const authStore = useAuthStore()

  const waitingRooms = roomStore.waitingRooms
  const myRooms = useMemo(() => roomStore.myRooms, [roomStore.myRooms])

  const SectionLabel = ({ icon, label, count }: { icon: React.ReactNode; label: string; count: number }) => (
    <div className="flex items-center gap-2 px-3 pt-4 pb-1">
      {icon}
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="ml-auto text-xs text-muted-foreground">{count}</span>
    </div>
  )

  return (
    <aside className="w-72 shrink-0 border-r bg-muted/30 flex flex-col h-full">
      <div className="px-3 py-2 border-b flex items-center gap-2">
        <MessageSquare className="h-4 w-4" />
        <div className="min-w-0">
          <div className="text-sm font-semibold leading-tight">チャット</div>
          <div className="text-[11px] text-muted-foreground truncate">
            {authStore.user?.name} として対応中
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto pb-4">
        <SectionLabel
          icon={<MessageSquare className="h-3.5 w-3.5 text-green-600" />}
          label="対応中"
          count={myRooms.length}
        />
        {myRooms.length === 0 && (
          <div className="px-3 py-1 text-xs text-muted-foreground">担当しているルームはありません</div>
        )}
        <div className="space-y-1 px-1">
          {myRooms.map((room) => (
            <RoomItem
              key={room.id}
              room={room}
              selected={selectedRoomId === room.id}
              onSelect={() => onSelectRoom(room.id)}
            />
          ))}
        </div>

        <SectionLabel
          icon={<Clock className="h-3.5 w-3.5 text-amber-600" />}
          label="未対応"
          count={waitingRooms.length}
        />
        {waitingRooms.length === 0 && (
          <div className="px-3 py-1 text-xs text-muted-foreground">待ちのルームはありません</div>
        )}
        <div className="space-y-1 px-1">
          {waitingRooms.map((room) => (
            <RoomItem
              key={room.id}
              room={room}
              selected={selectedRoomId === room.id}
              onSelect={() => onSelectRoom(room.id)}
            />
          ))}
        </div>
      </div>
    </aside>
  )
})

export { RoomSidebar }
export default RoomSidebar